"use client";

import { cn } from "@/lib/utils/cn";
import { createCalendar } from "@internationalized/date";
import { useRef } from "react";
import {
  type AriaDatePickerProps,
  type DateValue,
  useDateField,
  useLocale,
} from "react-aria";
import { useDateFieldState } from "react-stately";
import { DateSegment } from "./DateSegment";

function DateField(
  props: AriaDatePickerProps<DateValue> & {
    className?: string;
    dataTestId?: string;
  },
) {
  const { className, dataTestId, ...restProps } = props;
  const ref = useRef<HTMLDivElement | null>(null);

  const { locale } = useLocale();
  const state = useDateFieldState({
    ...restProps,
    locale,
    createCalendar,
    isDisabled: restProps.isDisabled,
  });
  const { fieldProps } = useDateField(restProps, state, ref);

  return (
    <div
      {...fieldProps}
      ref={ref}
      className={cn(
        "border-input bg-card ring-offset-background focus-visible:ring-ring inline-flex h-10 flex-1 items-center rounded-l-md border border-r-0 py-2 pl-3 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
        restProps.isDisabled && "cursor-not-allowed opacity-50",
        className,
      )}
      data-testid={dataTestId}
    >
      {state.segments.map((segment, i) => (
        <DateSegment key={i} segment={segment} state={state} />
      ))}
      {state.isInvalid && <span aria-hidden="true">🚫</span>}
    </div>
  );
}

export { DateField };
